import { useEffect } from 'react';
import { usePrevious } from 'react-use';

import { IntentDetection, intentDetectionApi } from 'apis/intentDetection';
import { useFeatureFlag } from 'utils/featureFlagging/useFeatureFlag';

import { FlowNodeTypes } from '../components/types';
import { FlowAction } from '../types';

/**
 * A hook that fetches a list of intent detections based on the nodes available
 * in the flow.
 */
export const useIntentDetectionFetch = (
  actions: FlowAction[] | null,
): readonly IntentDetection[] => {
  const flags = useFeatureFlag(['intent-detection']);
  const previousActions = usePrevious(actions);

  const [readIntentDetections, { intentDetections }] =
    intentDetectionApi.useLazyReadIntentDetectionsQuery({
      selectFromResult: ({ data }) => ({
        intentDetections: data?.intentDetections ?? [],
      }),
    });

  // Handles fetching the all intent detections, if the feature-flag is enabled and if there are any
  // intent detection nodes in the flow.
  useEffect(() => {
    if (!flags.intentDetection || previousActions === actions) return;

    const nodeIntentDetectionIds = getNodeIntentDetectionIds(actions);
    const previousNodeIntentDetectionIds =
      getNodeIntentDetectionIds(previousActions);

    if (
      nodeIntentDetectionIds.length &&
      !previousNodeIntentDetectionIds.length
    ) {
      // The actions can change for many reasons, e.g. changing x-y coords of the node, so we grab
      // the intents from the cache if they are already fetched to avoid too many API calls.
      const preferCacheValue = true;
      readIntentDetections(undefined, preferCacheValue);
    }
  }, [actions, flags.intentDetection, previousActions, readIntentDetections]);

  return intentDetections;
};

export function getNodeIntentDetectionIds(
  actions?: FlowAction[] | null,
): string[] {
  if (!actions) return [];

  const set = actions.reduce<Set<string>>((acc, { nodeType, payload }) => {
    if (nodeType === FlowNodeTypes.INTENT_DETECTION) {
      payload.intentDetections.forEach((i) => acc.add(i.id));
    }
    return acc;
  }, new Set<string>([]));

  return Array.from(set);
}
